import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';

const card = { boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' };

const STATUS_STYLES = {
  campaign_active: 'bg-blue-50 text-blue-600 border border-blue-200',
  to_be_settled:   'bg-amber-50 text-amber-700 border border-amber-200',
  paid:            'bg-[#ecf8fb] text-[#2a798c] border border-[#3fb5d2]/30',
};
const STATUS_LABELS = {
  campaign_active: 'Campaign Active',
  to_be_settled:   'To Be Settled',
  paid:            'Paid',
};

const Payouts = () => {
  const { profile } = useAuth();
  const [payouts, setPayouts]       = useState([]);
  const [allPayouts, setAllPayouts] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState({});

  useEffect(() => { loadPayouts(); }, [statusFilter]); // eslint-disable-line

  const loadPayouts = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/payouts');
      setAllPayouts(data || []);
      let filtered = data || [];
      if (statusFilter === 'campaign_active') filtered = filtered.filter(p => p.campaigns?.status === 'active');
      else if (statusFilter === 'to_be_settled') filtered = filtered.filter(p => p.status === 'to_be_settled');
      else if (statusFilter === 'paid')          filtered = filtered.filter(p => p.status === 'paid');
      setPayouts(filtered);
    } catch (error) {
      alert('Error loading payouts: ' + (error.message || 'Unknown error'));
      setPayouts([]); setAllPayouts([]);
    } finally { setLoading(false); }
  };

  const handleUpdateStatus = async (payoutId, newStatus) => {
    if (!window.confirm(`Ubah status payout menjadi "${newStatus === 'paid' ? 'Paid' : 'To Be Settled'}"?`)) return;
    setUpdatingStatus(s => ({ ...s, [payoutId]: true }));
    try {
      await api.patch(`/api/payouts/${payoutId}/status`, { status: newStatus });
      await loadPayouts();
    } catch (error) { alert('Error: ' + (error.message || 'Unknown error')); }
    finally { setUpdatingStatus(s => ({ ...s, [payoutId]: false })); }
  };

  const fmtCurrency = (n) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(n);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#3fb5d2] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const summary = {
    campaign_active: allPayouts.filter(p => p.campaigns?.status === 'active').reduce((s, p) => s + parseFloat(p.amount || 0), 0),
    to_be_settled:   allPayouts.filter(p => p.status === 'to_be_settled').reduce((s, p) => s + parseFloat(p.amount || 0), 0),
    paid:            allPayouts.filter(p => p.status === 'paid').reduce((s, p) => s + parseFloat(p.amount || 0), 0),
    total:           allPayouts.reduce((s, p) => s + parseFloat(p.amount || 0), 0),
  };
  const counts = {
    campaign_active: allPayouts.filter(p => p.campaigns?.status === 'active').length,
    to_be_settled:   allPayouts.filter(p => p.status === 'to_be_settled').length,
    paid:            allPayouts.filter(p => p.status === 'paid').length,
    total:           allPayouts.length,
  };

  const filters = ['all', 'campaign_active', 'to_be_settled', 'paid'];
  const filterLabels = { all: 'All', campaign_active: 'Campaign Active', to_be_settled: 'To Be Settled', paid: 'Paid' };

  return (
    <div className="space-y-4">
      <h1 className="text-[22px] font-semibold text-[#3b3b3b] leading-8">Payouts</h1>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { key: 'campaign_active', label: 'Campaign Active', color: 'text-blue-600' },
          { key: 'to_be_settled',   label: 'To Be Settled',   color: 'text-amber-600' },
          { key: 'paid',            label: 'Paid',            color: 'text-[#2a798c]' },
          { key: 'total',           label: 'Total',           color: 'text-[#3b3b3b]' },
        ].map(({ key, label, color }) => (
          <div key={key} className="bg-white rounded-2xl p-5" style={card}>
            <p className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-[20px] font-bold ${color} leading-tight`}>{fmtCurrency(summary[key])}</p>
            <p className="text-[11px] text-[#8a8a8a] mt-1">{counts[key]} payout</p>
          </div>
        ))}
      </div>

      {/* Filter tabs */}
      <div className="bg-white rounded-2xl px-4 py-3 flex items-center gap-2 flex-wrap" style={card}>
        {filters.map(f => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={`px-4 py-1.5 rounded-lg text-[13px] font-medium transition-colors ${
              statusFilter === f
                ? 'bg-[#3fb5d2] text-white'
                : 'bg-gray-100 text-[#646464] hover:bg-gray-200'
            }`}
          >
            {filterLabels[f]}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl" style={card}>
        {payouts.length === 0 ? (
          <p className="text-center text-[#8a8a8a] text-[14px] py-12">Tidak ada payout ditemukan.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#e5e5e5]">
                  {['Hunter','Campaign','Post Link','Play Count','Tier','Jenjang','Amount','Status','Created At',
                    ...(profile?.role === 'admin' || profile?.role === 'management' ? ['Actions'] : [])
                  ].map(h => (
                    <th key={h} className="text-[11px] font-medium text-[#8a8a8a] uppercase tracking-wider px-4 py-3 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {payouts.map(p => (
                  <tr key={p.id} className="border-b border-[#e5e5e5] last:border-0 hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 text-[13px] text-[#3b3b3b] whitespace-nowrap">
                      {p.user_profiles?.full_name || p.user_profiles?.email || '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <p className="text-[13px] text-[#3b3b3b]">{p.campaigns?.name || '—'}</p>
                      {p.campaigns?.status && (
                        <p className="text-[11px] text-[#8a8a8a]">{p.campaigns.status}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 max-w-[160px]">
                      {p.proposals?.social_media_link ? (
                        <a href={p.proposals.social_media_link} target="_blank" rel="noopener noreferrer"
                          className="text-[#3fb5d2] hover:text-[#2a798c] text-[13px] block truncate transition-colors"
                          title={p.proposals.social_media_link}>
                          {p.proposals.social_media_link}
                        </a>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#3b3b3b] whitespace-nowrap">
                      {p.proposals?.play_count?.toLocaleString('id-ID') || 0}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#3b3b3b] whitespace-nowrap">
                      {p.tiers?.nama || '—'}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[#3b3b3b] whitespace-nowrap">
                      {p.tier_payouts ? (
                        <span>J{p.tier_payouts.level_order} <span className="text-[11px] text-[#8a8a8a]">({p.tier_payouts.jumlah_view.toLocaleString('id-ID')}v)</span></span>
                      ) : <span className="text-[11px] text-[#8a8a8a]">Bounty</span>}
                    </td>
                    <td className="px-4 py-3 text-[13px] font-semibold text-[#3b3b3b] whitespace-nowrap">
                      {fmtCurrency(p.amount)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${STATUS_STYLES[p.status] || 'bg-gray-100 text-[#646464]'}`}>
                        {STATUS_LABELS[p.status] || p.status}
                      </span>
                      {p.paid_at && <p className="text-[11px] text-[#8a8a8a] mt-0.5">{fmtDate(p.paid_at)}</p>}
                    </td>
                    <td className="px-4 py-3 text-[12px] text-[#8a8a8a] whitespace-nowrap">{fmtDate(p.created_at)}</td>
                    {(profile?.role === 'admin' || profile?.role === 'management') && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        {p.status === 'to_be_settled' && (
                          <button
                            onClick={() => handleUpdateStatus(p.id, 'paid')}
                            disabled={updatingStatus[p.id]}
                            className="inline-flex items-center gap-1 bg-[#3fb5d2] hover:bg-[#2a798c] text-white text-[12px] font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                          >
                            {updatingStatus[p.id] ? 'Updating...' : 'Mark as Paid'}
                          </button>
                        )}
                        {p.status === 'paid' && (
                          <span className="text-[#2a798c] text-[12px] font-semibold">✓ Paid</span>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Payouts;
